/**
 * helpers.js
 * Description: exports helper functions
 */

import { keywords, literals, identifier } from "./constants.js";

// Prints error message along with line and index of error
export function generateError(message, token) {
  if (token)
    return `${message}\nError at line ${token.line} index ${token.index}`;
  return `${message}\n`;
}

// ---------- CHECKERS ----------

// [1] STRING
// Evaluates if a character is a whitespace
export function isSpace(char) {
  return /\s/.test(char);
}

// Evaluates if a string is a LOLCODE Keyword
export function isKeyword(string) {
  for (const type in keywords) if (keywords[type].test(string)) return type;
  return false;
}

// Evaluates if a string is a LOLCODE Literal
export function isLiteral(string) {
  for (const type in literals) if (literals[type].test(string)) return type;
  return false;
}

// Evaluates if a string is a LOLCODE Identifier
export function isVarident(string) {
  if (identifier.test(string)) return "IDENT";
  else return false;
}

// [2] TOKENS
// Checks if a token is a valid LOLCODE Mathematical Operator
export function isArithmeticOperatorToken(operation_token) {
  if (
    [
      "OP_ADD",
      "OP_SUB",
      "OP_MUL",
      "OP_DIV",
      "OP_MOD",
      "OP_MAX",
      "OP_MIN",
    ].includes(operation_token.type)
  )
    return true;
  return false;
}

// Checks if a token is a literal that is either a NUMBR or NUMBAR
export function isNumberLiteral(token) {
  if (["INT", "FLOAT"].includes(token.type)) return true;
  return false;
}

// Checks if a token is a literal that is either a WIN or FAIL
export function isBooleanLiteral(token) {
  if (token.type === "BOOL") return true;
  return false;
}

// Checks if a token is a keyword that requires evaluation to a Boolean value
export function isBoolean(token) {
  if (
    isBooleanLiteral(token) ||
    isUnaryOperatorToken(token) ||
    isBinaryBooleanOperatorToken(token)
  )
    return true;
  return false;
}

// Checks if a token is a valid NOT token
export function isUnaryOperatorToken(operation_token) {
  if (operation_token && keywords.OP_NOT.test(operation_token.token))
    return true;
  return false;
}

// Checks if a token is a valid AND, OR, XOR, EQU, NEQ token
export function isBinaryBooleanOperatorToken(operation_token) {
  if (
    operation_token &&
    ["OP_AND", "OP_OR", "OP_XOR", "OP_EQU", "OP_NEQ"].includes(
      operation_token.type
    )
  )
    return true;
  return false;
}

// Checks if a token is a valid ANY or ALL token
export function isInfOperatorToken(infop_token) {
  if (
    infop_token &&
    (keywords.OP_ANY.test(infop_token.token) ||
      keywords.OP_ALL.test(infop_token.token))
  )
    return true;
  return false;
}

// Checks if a token is a valid AN token
export function isAnToken(an_token) {
  if (an_token && keywords.MULOP_SEP.test(an_token.token)) return true;
  return false;
}

// Checks if a token is a valid ITZ token
export function isItzToken(token) {
  if (token && token.type === "VAR_INIT") return true;
  return false;
}

// Checks if a token is a valid R token
export function isRToken(token) {
  if (token && token.type === "VAR_ASSIGN") return true;
  return false;
}

// // Checks if a token is a valid GIMMEH token
// export function isGimmehToken(token) {
//   if (token && token.type === "INPUT") return true;
//   return false;
// }

// // Checks if a token is a valid VISIBLE token
// export function isVisibleToken(token) {
//   if (token && token.type === "OUTPUT") return true;
//   return false;
// }

// Checks if a token is a literal
export function isLiteralToken(token) {
  if (token && ["INT", "FLOAT", "STRING", "BOOL"].includes(token.type))
    return true;
  return false;
}

// Checks if a token is an identifier
export function isIdentifierToken(token) {
  if (token && ["VAR_IT", "IDENT"].includes(token.type)) return true;
  return false;
}

// Checks if a token is a valid expression
export function isExpressionToken(token) {
  if (
    token &&
    (token.type.slice(0, 3) === "OP_" ||
      isLiteralToken(token) ||
      isIdentifierToken(token))
  )
    return true;
  return false;
}

// ---------- Tokenizer Helpers ----------

// Removes whitespaces
export function trimWhitespace(index, line) {
  while (isSpace(line[index])) index++;
  return index;
}

// ---------- Parser Helpers ----------

// Checks if the next token's type is the expected type
export function checkNextToken(index, tokens, expectedType) {
  let nextToken = tokens[index + 1];

  if (nextToken && nextToken.type === expectedType) return nextToken;
  else throw new Error(generateError(`Expected ${expectedType}.`, nextToken));
}

// For grammars involving single tokens only
// Don't use for expressions
export function acceptNextToken(index, parentToken, childToken) {
  parentToken.value.push(childToken);
  return index + 1;
}

// ---------- Semantiker Helpers ----------

// Gets the value of a variable from the symbol table
// Throws error if variable is not in the symbol table
export function getSymbol(symbolTable, symbolToken) {
  let symbol = symbolTable[symbolToken.token];
  if (symbolToken.token in symbolTable) return symbol;
  else
    throw new Error(
      generateError(`Variable ${symbolToken.token} uninitialized.`, symbolToken)
    );
}

// Verifies that the current value of a variable is the appropriate type
export function verifyValueType(symbolTable, token, type) {
  let value = getSymbol(symbolTable, token);
  if (typeof value === type) return value;
  else
    throw new Error(
      generateError(`Expected type: INT/FLOAT. Got ${token.type}`, token)
    );
}

// Converts a token's token attribute to its proper type
// If type === int/float, return token as Number
// Else if type === boolean, return win/fail as true/false
// Else if type === string, return token without double quotes
export function properType(token) {
  if (token.type === "INT" || token.type === "FLOAT")
    return Number(token.token);
  else if (token.type === "BOOL") return token.token === "WIN" ? true : false;
  else if (token.type === "STRING")
    return token.token.slice(1, token.token.length - 1);
}

// Helper function to evaluate XOR operations
export function XOR(value1, value2) {
  return (value1 || value2) && !(value1 && value2);
}
